import { isUnaryFilterOperator, type AttributeFilterState } from './attribute-filter';
import { operatorsInFamily, type OperatorOption } from './operators';

/**
 * The device attributes with dedicated editor behavior: their own operator
 * list, and (for some) a fixed value select. Everything else — raw device
 * attributes and live-notification markers — edits through the shared
 * operator/value editor with free text.
 */
export const SPECIAL_DEVICE_ATTRIBUTES = [
  'id',
  'platform',
  'app_identifier',
  '_last_status',
  '_last_used',
  'device_model',
] as const;

export type SpecialDeviceAttribute = (typeof SPECIAL_DEVICE_ATTRIBUTES)[number];

export function specialDeviceAttributeOf(name: string): SpecialDeviceAttribute | null {
  return SPECIAL_DEVICE_ATTRIBUTES.find((attribute) => attribute === name) ?? null;
}

export type SpecialDeviceAttributeConfig = {
  label: string;
  defaultOperator: string;
  defaultValue?: string;
  operators: OperatorOption[];
  values: OperatorOption[];
};

const EQUALITY_OPERATORS = operatorsInFamily('equality');
const EXISTENCE_OPERATORS = operatorsInFamily('existence');

const LAST_STATUS_OPERATORS: OperatorOption[] = [
  { label: 'is', value: 'eq' },
  { label: 'is not', value: '!eq' },
];

const LAST_USED_OPERATORS: OperatorOption[] = [
  { label: 'is after', value: 'timestamp_gt' },
  { label: 'is before', value: 'timestamp_lt' },
  { label: 'is between', value: 'timestamp_bw' },
  { label: 'has not been used', value: '!exists' },
];

const SPECIAL_DEVICE_ATTRIBUTE_CONFIG = {
  id: {
    label: 'exists',
    defaultOperator: 'exists',
    operators: EXISTENCE_OPERATORS,
    values: [
      { value: 'true', label: 'true' },
      { value: 'false', label: 'false' },
    ],
  },
  platform: {
    label: 'platform',
    defaultOperator: 'eq',
    defaultValue: 'ios',
    operators: EQUALITY_OPERATORS,
    values: [
      { value: 'ios', label: 'iOS' },
      { value: 'android', label: 'Android' },
    ],
  },
  // The `app` filter is only surfaced when the `multi_app` feature is enabled.
  // Its values are the workspace's configured apps, injected at render time
  // rather than hardcoded here like the other special attributes.
  app_identifier: {
    label: 'app_identifier',
    defaultOperator: 'eq',
    operators: EQUALITY_OPERATORS,
    values: [],
  },
  _last_status: {
    label: 'last status',
    defaultOperator: 'eq',
    defaultValue: 'bounced',
    operators: LAST_STATUS_OPERATORS,
    values: [
      { value: 'bounced', label: 'bounced' },
      { value: 'suppressed', label: 'suppressed' },
      { value: 'sent', label: 'sent' },
      { value: 'unused', label: 'unused' },
    ],
  },
  _last_used: {
    label: 'last used',
    defaultOperator: 'timestamp_gt',
    operators: LAST_USED_OPERATORS,
    values: [],
  },
  device_model: {
    label: 'device model',
    defaultOperator: 'eq',
    operators: [...EQUALITY_OPERATORS, ...EXISTENCE_OPERATORS],
    values: [],
  },
} satisfies Record<SpecialDeviceAttribute, SpecialDeviceAttributeConfig>;

export function specialDeviceAttributeConfig(attribute: SpecialDeviceAttribute): SpecialDeviceAttributeConfig {
  return SPECIAL_DEVICE_ATTRIBUTE_CONFIG[attribute];
}

/**
 * The value/operator codec for `_last_status` value picks. The wire encodes
 * "unused" as an existence operator, never as a value: !exists ≡ is unused,
 * exists ≡ is not unused. Picking 'unused' from a real status maps the
 * equality operator to its existence spelling; picking a real status from
 * "unused" maps back.
 *
 * Deliberate deviation from the Ember row (#lastStatusPatchFor): re-picking
 * 'unused' while the operator is already an existence operator must keep it.
 * Ember maps the wire operator through the eq/!eq branch ('exists' !== '!eq'
 * → '!exists'), silently flipping "is not unused" into "is unused".
 */
export function lastStatusPatchFor(state: AttributeFilterState, value: string): Partial<AttributeFilterState> {
  if (value === 'unused') {
    if (isUnaryFilterOperator(state.operator)) {
      // Already the unused encoding; nothing to change.
      return { value: null, value2: null };
    }
    return { operator: state.operator === '!eq' ? 'exists' : '!exists', value: null, value2: null };
  }
  if (isUnaryFilterOperator(state.operator)) {
    return { operator: state.operator === '!exists' ? 'eq' : '!eq', value };
  }
  return { value };
}

// --- app_identifier options ---

export type AppConditionOption = { value: string; label: string; description: string };

type AppPlatformLike = { platform: string; identifier: string };
type AppLike = { name: string; platforms: readonly AppPlatformLike[] };

const PLATFORM_LABELS = new Map<string, string>([
  ['ios', 'iOS'],
  ['android', 'Android'],
]);

/**
 * One option per app identifier a device can report. The condition value is
 * the platform identifier (the bundle/package id a device reports via its
 * `app_identifier` attribute) — the value the backend indexes conditions
 * against. An app's iOS and Android platforms can carry different
 * identifiers, so each configured identifier is its own option, labeled by
 * the identifier with the app name and platform as the description.
 */
export function appConditionOptions(apps: readonly AppLike[]): AppConditionOption[] {
  const byIdentifier = new Map<string, { appName: string; platforms: string[] }>();
  for (const app of apps) {
    for (const { platform, identifier } of app.platforms) {
      // Platforms without an identifier can't be targeted.
      if (identifier === '') {
        continue;
      }
      const entry = byIdentifier.get(identifier);
      if (entry == null) {
        byIdentifier.set(identifier, { appName: app.name, platforms: [platform] });
      } else {
        entry.platforms.push(platform);
      }
    }
  }
  return [...byIdentifier].map(([identifier, { appName, platforms }]) => ({
    value: identifier,
    label: identifier,
    description: `${appName} (${platforms.map((platform) => PLATFORM_LABELS.get(platform) ?? platform).join(', ')})`,
  }));
}

// --- live-notification (_cio_pts_*) marker labels ---

export const LIVE_NOTIFICATION_MARKER_PREFIX = '_cio_pts_';

type LiveNotificationRegistrationLike = {
  id: number;
  name?: string | null;
  type: string;
  platform: string;
};

export type LiveNotificationMarkerDetails = { name: string; fallbackSource: string; platforms: string[] };

/**
 * Details for one `_cio_pts_` marker suffix: either a registration id (new
 * format, e.g. '5') or a type string (e.g. 'io.customer.delivery'). A
 * registration's per-platform rows aggregate into one entry.
 */
// fallow-ignore-next-line complexity -- one lookup per marker format, with the deleted-registration fallback
export function liveNotificationMarkerDetails(
  markerSuffix: string,
  registrations: readonly LiveNotificationRegistrationLike[],
): LiveNotificationMarkerDetails {
  const byId = registrations.filter((registration) => String(registration.id) === markerSuffix);
  if (byId.length > 0) {
    const [first] = byId;
    return {
      name: first?.name ?? '',
      fallbackSource: first?.type ?? markerSuffix,
      platforms: byId.map((registration) => registration.platform),
    };
  }
  const byType = registrations.filter((registration) => registration.type === markerSuffix);
  if (byType.length > 0) {
    return {
      name: byType.find((registration) => (registration.name ?? '') !== '')?.name ?? '',
      fallbackSource: markerSuffix,
      platforms: byType.map((registration) => registration.platform),
    };
  }
  if (!Number.isNaN(Number(markerSuffix))) {
    // An id-format marker whose registration no longer exists — the raw
    // numeric id would be meaningless as a label.
    return { name: `Live notification type ${markerSuffix}`, fallbackSource: markerSuffix, platforms: [] };
  }
  return { name: '', fallbackSource: markerSuffix, platforms: [] };
}

export type LiveNotificationMarkerOption = {
  value: string;
  label: string;
  icon?: 'apple' | 'android';
};

/**
 * Select options for the workspace's `_cio_pts_` marker attributes: the `*`
 * marker ("All live notification types") pinned first when present, the
 * specific types alphabetical by label.
 */
export function liveNotificationMarkerOptions(
  markerNames: readonly string[],
  registrations: readonly LiveNotificationRegistrationLike[],
): LiveNotificationMarkerOption[] {
  let allTypesOption: LiveNotificationMarkerOption | null = null;
  const specificOptions: LiveNotificationMarkerOption[] = [];
  for (const name of markerNames) {
    const suffix = name.slice(LIVE_NOTIFICATION_MARKER_PREFIX.length);
    if (suffix === '*') {
      allTypesOption = { label: 'All live notification types', value: name };
    } else {
      const details = liveNotificationMarkerDetails(suffix, registrations);
      const label = details.name.length > 0 ? details.name : liveNotificationTypeFallbackLabel(details.fallbackSource);
      const icons = liveNotificationTypePlatformIcons(details.platforms);
      specificOptions.push({ label, value: name, icon: icons.length === 1 ? icons[0] : undefined });
    }
  }
  specificOptions.sort((a, b) => a.label.localeCompare(b.label));
  return allTypesOption == null ? specificOptions : [allTypesOption, ...specificOptions];
}

/** The last dotted segment, capitalized — a readable stand-in for an unnamed type. */
export function liveNotificationTypeFallbackLabel(type: string): string {
  const segments = type.split('.').filter((segment) => segment.length > 0);
  const lastSegment = segments[segments.length - 1];
  if (lastSegment == null) {
    return type;
  }
  return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
}

export function liveNotificationTypePlatformIcons(platforms: readonly string[]): ('apple' | 'android')[] {
  const icons: ('apple' | 'android')[] = [];
  if (platforms.includes('ios')) {
    icons.push('apple');
  }
  if (platforms.includes('android')) {
    icons.push('android');
  }
  return icons;
}
